'use client'
import React, { useState, useRef, useEffect } from "react";
import styles from "@/components/BestSellers/best-sellers.module.css";
import Image from "next/image";
import Link from "next/link";
import badgeSrc from "@/assets/images/best-seller.svg";
import { bestSellersArray } from "./bestSellersData";

// Keep in sync with slugifyName() in lib/storefront.ts.
const slugify = (s) =>
    String(s ?? "")
        .toLowerCase()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

// `cars` arrives from the home layout with prices live-synced from the DB
// (see lib/landings.ts getBestSellers); the hardcoded array is the fallback.
// Same pattern as the storefront cards (carAlt in components/store/format.js).
const bestSellerAlt = (car) =>
    [car.year, car.model, car.transmission && car.transmission.toLowerCase(), "rental car in Georgia"]
        .filter(Boolean)
        .join(" ");

// Prices arrive in EUR from the ISR layout; convert them to the visitor's
// currency on the client with the same NBG table the storefront uses.
const CURRENCIES = {
    eur: { symbol: "€", key: "EUR" },
    usd: { symbol: "$", key: "USD" },
    gel: { symbol: "₾", key: "GEL" },
};

const readStoredCurrency = () => {
    try {
        return JSON.parse(localStorage.getItem("recordings") || "{}").currency;
    } catch {
        return undefined;
    }
};

const readUrlCurrency = () => new URLSearchParams(window.location.search).get("currency");

// `discountPercent` is the homepage-banner setting (Admin → Settings → Homepage
// banner): this line used to say "20%" whatever the banner said — or didn't.
export default function BestSellers({ cars, rates, discountPercent = null }) {
    const list = Array.isArray(cars) && cars.length ? cars : bestSellersArray;
    const [activeIndex, setActiveIndex] = useState(0);
    const containerRef = useRef(null);
    const [showNavButtons, setShowNavButtons] = useState(false);
    // 'eur' until mount so the server HTML and hydration match
    const [currency, setCurrency] = useState("eur");

    useEffect(() => {
        const apply = (code) => {
            if (CURRENCIES[code]) setCurrency(code);
        };
        apply(readUrlCurrency() || readStoredCurrency());
        // CurrencyChooser writes recordings.currency just before router.replace,
        // while the URL still holds the old value — so trust storage here.
        const onStorageUpdate = () => apply(readStoredCurrency());
        const onPopstate = () => apply(readUrlCurrency() || readStoredCurrency());
        window.addEventListener("storageUpdate", onStorageUpdate);
        window.addEventListener("popstate", onPopstate);
        return () => {
            window.removeEventListener("storageUpdate", onStorageUpdate);
            window.removeEventListener("popstate", onPopstate);
        };
    }, []);

    const cur = CURRENCIES[currency];
    const rate = Number(rates?.[cur.key]);
    const formatPrice = (price) =>
        rate > 0
            ? `${cur.symbol}${Math.round((price.eur ?? price.price) * rate)}`
            : `${price.currency}${price.price}`;

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const card = container.querySelector(`.${styles.car}`);
            const cardWidth = card ? card.getBoundingClientRect().width : container.clientWidth;
            const gap = parseFloat(getComputedStyle(container).columnGap || getComputedStyle(container).gap) || 0;
            const step = cardWidth + gap;
            const rawIndex = step ? Math.round(container.scrollLeft / step) : 0;
            const clamped = Math.max(0, Math.min(list.length - 1, rawIndex));
            setActiveIndex(clamped);
        };

        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, [list.length]);

    useEffect(() => {
        const updateVisibility = () => {
            const container = containerRef.current;
            if (!container) return;
            const scrollable = container.scrollWidth - 1 > container.clientWidth;
            setShowNavButtons(scrollable);
        };
        updateVisibility();
        window.addEventListener("resize", updateVisibility);
        return () => window.removeEventListener("resize", updateVisibility);
    }, []);

    const scrollByCard = (direction) => {
        const container = containerRef.current;
        if (!container) return;
        const card = container.querySelector(`.${styles.car}`);
        const cardWidth = card ? card.getBoundingClientRect().width : container.clientWidth;
        const gap = parseFloat(getComputedStyle(container).columnGap || getComputedStyle(container).gap) || 0;
        const amount = cardWidth + gap;
        container.scrollBy({ left: direction * amount, behavior: "smooth" });
    };

    const handleScrollUp = () => {
        if (typeof window === "undefined" || typeof document === "undefined") return;
        const target = document.querySelector('[data-scroll-target="search-bar"]');
        if (target) {
            try {
                target.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
            } catch {
                const rect = target.getBoundingClientRect();
                const offset = window.pageYOffset + rect.top - 12;
                window.scrollTo({ top: Math.max(0, offset), left: 0, behavior: "smooth" });
            }
            const fallbackOffset = (() => {
                const rect = target.getBoundingClientRect();
                return window.pageYOffset + rect.top - 12;
            })();
            setTimeout(() => {
                window.scrollTo({ top: Math.max(0, fallbackOffset), left: 0, behavior: "auto" });
            }, 700);
            return;
        }
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    };

    return (
        <div className={styles.bestSellersWrapper}>
            <h2 className={styles.bestSellersHeading}>Our Customer's Top 4 Favourites</h2>
            {Number(discountPercent) > 0 && (
                <div className={styles.discountMessage}>Book Now with <span className={styles.discountMessageDiscount}>{discountPercent}%</span> Discount</div>
            )}
            <div className={styles.bestSellersFrame}>
                <div ref={containerRef} className={styles.bestSellers}>
                    {list.map((car, carIndex) => (
                            <div key={car.id} className={styles.car}>
                                <div className={styles.bestSellerBadge}>
                                    <Image
                                        src={badgeSrc}
                                        alt=""
                                        width={56}
                                        height={56}
                                        priority
                                    />
                            </div>
                            <div className={styles.carImage}>
                                <Image
                                    src={car.image}
                                    alt={bestSellerAlt(car)}
                                    width={329}
                                    height={193}
                                    style={{ objectFit: "cover", width: "100%", height: "auto" }}
                                />
                            </div>
                            <div className={styles.carDetails}>
                                <div>
                                    <div className={styles.model}><Link href={`/cars/${car.slug || slugify(car.model)}`} className={styles.modelLink}>{car.model}</Link></div>
                                    <div className={styles.year}>{car.year}</div>
                                </div>
                                <div className={styles.transmission}>
                                    Transmission: <span className="ms-1">{car.transmission}</span>
                                </div>
                                <div className={styles.category}>{car.category}</div>
                                <div className={styles.prices}>
                                    {car.prices.map((price) => (
                                        <div key={price.id} className={styles.price}>
                                            <div className={styles.priceName}>
                                                {price.name.split(' ').map((word, i) => (
                                                    <span key={i} className={styles.priceNameSegment}>
                                                        {word}
                                                    </span>
                                                ))}
                                            </div>
                                            <div>{formatPrice(price)}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                <button
                    type="button"
                    className={`${styles.navButton} ${styles.navLeft}`}
                    aria-label="Scroll to previous popular vehicle"
                    onClick={() => scrollByCard(-1)}
                    style={{ display: showNavButtons ? "flex" : "none" }}
                >
                    &lt;
                </button>
                <button
                    type="button"
                    className={`${styles.navButton} ${styles.navRight}`}
                    aria-label="Scroll to next popular vehicle"
                    onClick={() => scrollByCard(1)}
                    style={{ display: showNavButtons ? "flex" : "none" }}
                >
                    &gt;
                </button>
            </div>
            <div className={styles.indicatorWrapper}>
                {list.map((_, index) => (
                    <div
                        key={index}
                        className={`${styles.indicatorSegment} ${activeIndex === index ? styles.active : ""
                            }`}
                    ></div>
                ))}
            </div>
            <div className={styles.scrollUpRow}>
                <button type="button" className={styles.scrollUpButton} onClick={handleScrollUp}>
                    Scroll up &amp; Book
                </button>
            </div>
        </div>
    );
}
